/**
 * undercover® — basisgedrag voor alle pagina's (±1 KB).
 * - scroll-reveal voor [data-uc-reveal] (IntersectionObserver)
 * - ripple-startpunt voor .uc-btn
 * - window.ucTrack(): custom events via Shopify.analytics.publish
 *   (loopt via Customer Events, dus via Shopify's toestemmingsbeheer)
 */
(() => {
  const root = document.documentElement;
  window.ucReady = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Scroll-reveal */
  const revealAll = (scope) =>
    scope.querySelectorAll('[data-uc-reveal]').forEach((el) => el.classList.add('is-revealed'));

  let observer = null;
  if ('IntersectionObserver' in window && !reduceMotion.matches) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
  }

  const observe = (scope = document) => {
    if (!observer) return revealAll(scope);
    scope.querySelectorAll('[data-uc-reveal]:not(.is-revealed)').forEach((el) => observer.observe(el));
  };

  // pas opzetten als de browser even niets te doen heeft (houdt de eerste render vrij)
  (window.requestIdleCallback || ((cb) => setTimeout(cb, 1)))(() => observe(), { timeout: 200 });

  /* Theme-editor: nieuwe of herladen secties direct tonen */
  document.addEventListener('shopify:section:load', (event) => revealAll(event.target));
  document.addEventListener('shopify:block:select', (event) => revealAll(event.target.closest('.shopify-section') || event.target));

  /* Ankerlinks: secties onder de vouw worden lui gerenderd (content-visibility), dus eerst
     alles renderen, dan pas scrollen, anders klopt de doelpositie niet. */
  const scrollToHash = (hash, smooth = true) => {
    let target = null;
    try { target = hash && hash.length > 1 ? document.querySelector(decodeURIComponent(hash)) : null; } catch { return false; }
    if (!target) return false;
    root.classList.add('uc-cv-off');
    // Horizon scrollt op desktop in .page-wrapper; scrollIntoView schiet daar door, dus zelf rekenen
    const wrapper = document.querySelector('.page-wrapper');
    const scroller = wrapper && wrapper.scrollHeight > wrapper.clientHeight + 5 ? wrapper : document.scrollingElement;
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 72;
    const base = scroller === document.scrollingElement ? 0 : scroller.getBoundingClientRect().top;
    const top = target.getBoundingClientRect().top - base + scroller.scrollTop - header - 12;
    scroller.scrollTo({ top, behavior: smooth && !reduceMotion.matches ? 'smooth' : 'instant' });
    setTimeout(() => root.classList.remove('uc-cv-off'), 1500);
    return true;
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link || event.defaultPrevented || link.target === '_blank') return;
    const url = new URL(link.href, location.href);
    if (url.pathname !== location.pathname || !url.hash) return;
    if (scrollToHash(url.hash)) {
      event.preventDefault();
      history.pushState(null, '', url.hash);
    }
  });

  if (location.hash) window.addEventListener('load', () => scrollToHash(location.hash, false), { once: true });

  /* Ripple vanaf het klikpunt */
  document.addEventListener('pointerdown', (event) => {
    const button = event.target.closest('.uc-btn');
    if (!button || reduceMotion.matches) return;
    const rect = button.getBoundingClientRect();
    button.style.setProperty('--x', `${event.clientX - rect.left}px`);
    button.style.setProperty('--y', `${event.clientY - rect.top}px`);
    button.classList.remove('is-rippling');
    void button.offsetWidth;
    button.classList.add('is-rippling');
  });

  document.addEventListener('animationend', (event) => {
    if (event.animationName === 'uc-ripple') event.target.classList.remove('is-rippling');
  });

  /* Meetbaar maken: ucTrack('quiz_completed', { result: 'hat' }) */
  window.ucTrack = (name, data = {}) => {
    try {
      window.Shopify?.analytics?.publish?.(name, data);
    } catch (error) {
      /* tracking mag nooit de winkel breken */
    }
  };

  root.classList.add('uc-ready');
})();
